/**
 * DrawerRenderer Component
 *
 * Renders all drawers from the DrawerStackContext based on their type.
 * Place this component inside a DrawerStackProvider to render the drawer stack.
 *
 * This component acts as a switch statement, rendering the appropriate
 * drawer component for each drawer type in the stack.
 *
 * @see docs/plans/DRAWER-SIDEBAR-CONVERSION-PLAN.md
 */

import React, { lazy, useCallback, memo } from "react";
import { useDrawerStack, type DrawerInstance } from "@/contexts/DrawerStackContext";
import { useAuth } from "@/contexts/AuthContext";
import { GenericDrawer } from "./GenericDrawer";
import type { TaskDrawerMode } from "./TaskDrawerContent";
import { FileText, MessageSquare, StickyNote, Calendar, ClipboardCheck, Activity, FileSignature, Building2, Palette, Briefcase, Wrench, Users, UserCog, BarChart3, FormInput, Mail, User } from "lucide-react";
import type { Project } from "@/types/project";

// ============================================================================
// Lazy-loaded Tab Components (for code splitting)
// ============================================================================

// CommunicationTab and ProjectNotesTab now use dedicated drawer content components
// See: CommunicationDrawerContent, NotesDrawerContent

// ActivityTab now uses dedicated drawer content component
// See: ActivityDrawerContent

const ConsultationsDrawerContent = lazy(() =>
  import("./ConsultationsDrawerContent").then((m) => ({ default: m.ConsultationsDrawerContent }))
);

const SiteSurveyTab = lazy(() =>
  import("@/components/project/tabs/SiteSurveyTab").then((m) => ({ default: m.SiteSurveyTab }))
);

const ContractsTab = lazy(() => import("@/components/project/tabs/ContractsTab"));

const LenderTab = lazy(() => import("@/components/project/tabs/LenderTab"));

// EmailTab now uses dedicated drawer content component
// See: EmailsDrawerContent

// WorkOrdersTab now uses dedicated drawer content component
// See: WorkOrdersDrawerContent

const SalesManagerInfoTab = lazy(() =>
  import("@/components/project/tabs/SalesManagerInfoTab").then((m) => ({ default: m.SalesManagerInfoTab }))
);

const ProjectManagementDrawerContent = lazy(() =>
  import("./ProjectManagementDrawerContent").then((m) => ({ default: m.ProjectManagementDrawerContent }))
);

const RequestDesignDrawerContent = lazy(() =>
  import("./RequestDesignDrawerContent").then((m) => ({ default: m.RequestDesignDrawerContent }))
);

const ContactInfoTab = lazy(() =>
  import("@/components/project/tabs/ContactInfoTab").then((m) => ({ default: m.ContactInfoTab }))
);

const DocumentsDrawerContent = lazy(() =>
  import("./DocumentsDrawerContent").then((m) => ({ default: m.DocumentsDrawerContent }))
);

const PartnerInfoTab = lazy(() =>
  import("@/components/project/tabs/PartnerInfoTab").then((m) => ({ default: m.PartnerInfoTab }))
);

// ReportsTab now uses dedicated drawer content component
// See: ReportsDrawerContent

const IntakeFormDrawerContent = lazy(() =>
  import("./IntakeFormDrawerContent").then((m) => ({ default: m.IntakeFormDrawerContent }))
);

const FinanceTabDrawerContent = lazy(() =>
  import("./FinanceTabDrawerContent").then((m) => ({ default: m.FinanceTabDrawerContent }))
);

// Keep FinanceDrawerContent for quick-view mode if needed in future
const FinanceDrawerContent = lazy(() =>
  import("./FinanceDrawerContent").then((m) => ({ default: m.FinanceDrawerContent }))
);

const TaskDrawerContent = lazy(() =>
  import("./TaskDrawerContent").then((m) => ({ default: m.TaskDrawerContent }))
);

const CommunicationDrawerContent = lazy(() =>
  import("./CommunicationDrawerContent").then((m) => ({ default: m.CommunicationDrawerContent }))
);

const EmailsDrawerContent = lazy(() =>
  import("./EmailsDrawerContent").then((m) => ({ default: m.EmailsDrawerContent }))
);

const ReportsDrawerContent = lazy(() =>
  import("./ReportsDrawerContent").then((m) => ({ default: m.ReportsDrawerContent }))
);

const NotesDrawerContent = lazy(() =>
  import("./NotesDrawerContent").then((m) => ({ default: m.NotesDrawerContent }))
);

const ActivityDrawerContent = lazy(() =>
  import("./ActivityDrawerContent").then((m) => ({ default: m.ActivityDrawerContent }))
);

const WorkOrdersDrawerContent = lazy(() =>
  import("./WorkOrdersDrawerContent").then((m) => ({ default: m.WorkOrdersDrawerContent }))
);

// ============================================================================
// Types
// ============================================================================

export interface DrawerRendererProps {
  /** Callback when any drawer animation completes */
  onAnimationComplete?: (drawerId: string, isOpen: boolean) => void;
}

// ============================================================================
// Drawer Content Configuration
// ============================================================================

interface DrawerConfig {
  title: string;
  icon: React.ReactNode;
  width: "sm" | "md" | "lg" | "xl" | "full";
  description: string;
}

const DRAWER_CONFIGS: Record<string, DrawerConfig> = {
  documents: {
    title: "Documents",
    icon: <FileText className="w-8 h-8" />,
    width: "xl",
    description: "View and manage project documents, uploads, and verification status.",
  },
  communication: {
    title: "Communication",
    icon: <MessageSquare className="w-8 h-8" />,
    width: "xl",
    description: "View project comments, messages, and communication history.",
  },
  notes: {
    title: "Project Notes",
    icon: <StickyNote className="w-8 h-8" />,
    width: "lg",
    description: "View and add notes for this project.",
  },
  consultations: {
    title: "Consultations",
    icon: <Calendar className="w-8 h-8" />,
    width: "xl",
    description: "View scheduled consultations and booking details.",
  },
  "site-survey": {
    title: "Site Survey",
    icon: <ClipboardCheck className="w-8 h-8" />,
    width: "xl",
    description: "View site survey details and checklists.",
  },
  activity: {
    title: "Activity",
    icon: <Activity className="w-8 h-8" />,
    width: "lg",
    description: "View project activity timeline and history.",
  },
  contracts: {
    title: "Contracts",
    icon: <FileSignature className="w-8 h-8" />,
    width: "xl",
    description: "View and manage project contracts.",
  },
  lender: {
    title: "Lender",
    icon: <Building2 className="w-8 h-8" />,
    width: "lg",
    description: "View lender information and financing details.",
  },
  designs: {
    title: "Designs",
    icon: <Palette className="w-8 h-8" />,
    width: "xl",
    description: "View and request design documents.",
  },
  "project-management": {
    title: "Project Management",
    icon: <Briefcase className="w-8 h-8" />,
    width: "xl",
    description: "View project management details and milestones.",
  },
  "work-orders": {
    title: "Work Orders",
    icon: <Wrench className="w-8 h-8" />,
    width: "xl",
    description: "View and manage work orders for this project.",
  },
  partner: {
    title: "Partner Info",
    icon: <Users className="w-8 h-8" />,
    width: "lg",
    description: "View partner and installer information.",
  },
  "sales-team": {
    title: "Sales Team",
    icon: <UserCog className="w-8 h-8" />,
    width: "lg",
    description: "View sales team assignments.",
  },
  reports: {
    title: "Reports",
    icon: <BarChart3 className="w-8 h-8" />,
    width: "xl",
    description: "View project reports and analytics.",
  },
  "intake-form": {
    title: "Project Intake Form",
    icon: <FormInput className="w-8 h-8" />,
    width: "xl",
    description: "View the project intake form details.",
  },
  emails: {
    title: "Emails",
    icon: <Mail className="w-8 h-8" />,
    width: "xl",
    description: "View email communications for this project.",
  },
  contact: {
    title: "Contact",
    icon: <User className="w-8 h-8" />,
    width: "lg",
    description: "View and edit contact information.",
  },
};

// ============================================================================
// Placeholder Content Component
// ============================================================================

interface PlaceholderContentProps {
  config: DrawerConfig;
  projectName?: string;
}

function PlaceholderContent({ config, projectName }: PlaceholderContentProps) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="p-4 rounded-full bg-muted/50 text-muted-foreground mb-4">
        {config.icon}
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">
        {config.title}
      </h3>
      {projectName && (
        <p className="text-sm text-muted-foreground mb-4">
          {projectName}
        </p>
      )}
      <p className="text-sm text-muted-foreground max-w-md">
        {config.description}
      </p>
      <div className="mt-6 px-4 py-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
        <p className="text-sm text-amber-700 dark:text-amber-300">
          Content integration coming soon
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Drawer Content Switcher
// ============================================================================

interface DrawerContentProps {
  drawer: DrawerInstance;
}

/**
 * Renders the appropriate drawer component based on type.
 * Add new drawer types here as they are implemented.
 *
 * Memoized to prevent re-renders when drawer context changes but this
 * specific drawer instance hasn't changed.
 */
const DrawerContent = memo(function DrawerContent({ drawer }: DrawerContentProps) {
  const { user } = useAuth();
  const projectId = drawer.props.projectId as string;
  const project = drawer.props.project as Project | undefined;
  const projectName = (project as Record<string, unknown>)?.project_name as string | undefined;

  // No-op callback for tabs that need onProjectUpdate but we don't need to handle updates
  const handleProjectUpdate = useCallback(() => {
    // In drawer context, we don't need to propagate updates back to parent
    // The drawer will refresh its own data as needed
  }, []);

  switch (drawer.type) {
    // =========================================================================
    // Core Drawers (Fully Implemented)
    // =========================================================================

    case "task": {
      const taskMode = (drawer.props.mode as TaskDrawerMode) || "create";
      const taskTitle = taskMode === "create" ? "Create Task" : "Task Details";
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="task"
          title={taskTitle}
          subtitle={projectName}
          width="lg"
          onClose={drawer.onClose}
        >
          {!projectId ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <TaskDrawerContent
              drawerId={drawer.id}
              mode={taskMode}
              projectId={projectId}
              taskId={drawer.props.taskId as string | undefined}
              onSuccess={drawer.props.onSuccess as ((task: unknown) => void) | undefined}
              onRequestClose={() => drawer.onClose?.()}
            />
          )}
        </GenericDrawer>
      );
    }

    case "finance":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="finance"
          title="Finance"
          subtitle={projectName}
          width="xl"
          onClose={drawer.onClose}
        >
          {!project ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <FinanceTabDrawerContent
              project={project}
              currentUserId={user?.id || ""}
              formValues={drawer.props.formValues as Partial<Project> | undefined}
              onInputChange={drawer.props.handleInputChange as ((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void) | undefined}
              onSelectChange={drawer.props.handleSelectChange as ((name: string, value: string) => void) | undefined}
              onFileChange={drawer.props.handleFileChange as ((e: React.ChangeEvent<HTMLInputElement>) => void) | undefined}
              onDrag={drawer.props.handleDrag as ((e: React.DragEvent, field: string, active: boolean) => void) | undefined}
              onDrop={drawer.props.handleDrop as ((e: React.DragEvent, field: string) => void) | undefined}
              onRemoveFile={drawer.props.handleRemoveFile as ((fieldName: keyof Project, index: number) => Promise<void>) | undefined}
              dragActive={drawer.props.dragActive as Record<string, boolean> | undefined}
              filePreviewUrls={drawer.props.filePreviewUrls as Record<string, string[]> | undefined}
              uploadStatus={drawer.props.uploadStatus as Record<string, string> | undefined}
            />
          )}
        </GenericDrawer>
      );

    // =========================================================================
    // Integrated Content Drawers (Phase 2)
    // =========================================================================

    case "communication":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="communication"
          title="Communication"
          subtitle={projectName}
          width="xl"
          onClose={drawer.onClose}
        >
          {!project ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <CommunicationDrawerContent project={project} />
          )}
        </GenericDrawer>
      );

    case "notes":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="notes"
          title="Project Notes"
          subtitle={projectName}
          width="lg"
          onClose={drawer.onClose}
        >
          {!project?.id ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <NotesDrawerContent projectId={project.id} />
          )}
        </GenericDrawer>
      );

    case "activity":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="activity"
          title="Activity"
          subtitle={projectName}
          width="lg"
          onClose={drawer.onClose}
        >
          {!project ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <ActivityDrawerContent project={project} />
          )}
        </GenericDrawer>
      );

    case "consultations":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="consultations"
          title="Consultations"
          subtitle={projectName}
          width="xl"
          onClose={drawer.onClose}
        >
          {!project?.id ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <ConsultationsDrawerContent projectId={project.id} project={project} />
          )}
        </GenericDrawer>
      );

    case "site-survey":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="site-survey"
          title="Site Survey"
          subtitle={projectName}
          width="xl"
          onClose={drawer.onClose}
        >
          {!project ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <SiteSurveyTab project={project} />
          )}
        </GenericDrawer>
      );

    case "contracts":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="contracts"
          title="Contracts"
          subtitle={projectName}
          width="xl"
          onClose={drawer.onClose}
        >
          {!project ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <ContractsTab project={project} />
          )}
        </GenericDrawer>
      );

    case "lender":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="lender"
          title="Lender"
          subtitle={projectName}
          width="lg"
          onClose={drawer.onClose}
        >
          {!project ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <LenderTab project={project} />
          )}
        </GenericDrawer>
      );

    case "emails":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="emails"
          title="Emails"
          subtitle={projectName}
          width="xl"
          onClose={drawer.onClose}
        >
          {!project?.id ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <EmailsDrawerContent projectId={project.id} />
          )}
        </GenericDrawer>
      );

    case "work-orders":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="work-orders"
          title="Work Orders"
          subtitle={projectName}
          width="xl"
          onClose={drawer.onClose}
        >
          {!project || !user?.id ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <WorkOrdersDrawerContent project={project} currentUserId={user.id} />
          )}
        </GenericDrawer>
      );

    case "sales-team":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="sales-team"
          title="Sales Team"
          subtitle={projectName}
          width="lg"
          onClose={drawer.onClose}
        >
          {!project ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <SalesManagerInfoTab project={project} onProjectUpdate={handleProjectUpdate} />
          )}
        </GenericDrawer>
      );

    case "project-management":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="project-management"
          title="Project Management"
          subtitle={projectName}
          width="xl"
          onClose={drawer.onClose}
        >
          {!project ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <ProjectManagementDrawerContent
              project={project}
              onProjectUpdate={handleProjectUpdate}
              documentUpdateTimestamp={drawer.props.documentUpdateTimestamp as number | undefined}
            />
          )}
        </GenericDrawer>
      );

    case "designs":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="designs"
          title="Design Requests"
          subtitle={projectName}
          width="xl"
          onClose={drawer.onClose}
        >
          {!project ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <RequestDesignDrawerContent
              drawerId={drawer.id}
              project={project}
              formValues={drawer.props.formValues as Partial<Project> | undefined}
              onTaskSubmitted={drawer.props.onTaskSubmitted as (() => void) | undefined}
            />
          )}
        </GenericDrawer>
      );

    case "contact":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="contact"
          title="Contact"
          subtitle={projectName}
          width="lg"
          onClose={drawer.onClose}
        >
          {!project ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <ContactInfoTab project={project} onProjectUpdate={handleProjectUpdate} />
          )}
        </GenericDrawer>
      );

    case "documents":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="documents"
          title="Documents"
          subtitle={projectName}
          width="xl"
          onClose={drawer.onClose}
        >
          {!project?.id ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <DocumentsDrawerContent projectId={project.id} project={project} />
          )}
        </GenericDrawer>
      );

    case "partner":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="partner"
          title="Partner Info"
          subtitle={projectName}
          width="xl"
          onClose={drawer.onClose}
        >
          {!project ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <PartnerInfoTab project={project} onProjectUpdate={handleProjectUpdate} />
          )}
        </GenericDrawer>
      );

    case "reports":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="reports"
          title="Reports"
          subtitle={projectName}
          width="xl"
          onClose={drawer.onClose}
        >
          {!project?.id ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <ReportsDrawerContent
              projectId={project.id}
              pipelineId={project.stages?.pipeline_id || 1}
            />
          )}
        </GenericDrawer>
      );

    case "intake-form":
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType="intake-form"
          title="Project Intake Form"
          subtitle={projectName}
          width="xl"
          onClose={drawer.onClose}
        >
          {!project?.id ? (
            <div className="p-6 text-center text-muted-foreground">
              Project data not available
            </div>
          ) : (
            <IntakeFormDrawerContent projectId={project.id} project={project} />
          )}
        </GenericDrawer>
      );

    // =========================================================================
    // Unknown Drawer Type
    // =========================================================================

    default:
      return (
        <GenericDrawer
          drawerId={drawer.id}
          drawerType={drawer.type}
          title="Unknown"
          width="lg"
          onClose={drawer.onClose}
        >
          <div className="p-6 text-center text-muted-foreground">
            Unknown drawer type: {drawer.type}
          </div>
        </GenericDrawer>
      );
  }
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if drawer ID, type, or props changed meaningfully
  const prev = prevProps.drawer;
  const next = nextProps.drawer;

  return (
    prev.id === next.id &&
    prev.type === next.type &&
    prev.layer === next.layer &&
    prev.props.projectId === next.props.projectId &&
    // Don't compare project object deeply - just check if it exists
    !!prev.props.project === !!next.props.project
  );
});

DrawerContent.displayName = "DrawerContent";

// ============================================================================
// Component
// ============================================================================

// ============================================================================
// Main Component
// ============================================================================

export function DrawerRenderer({ onAnimationComplete }: DrawerRendererProps) {
  const { drawers } = useDrawerStack();

  if (drawers.length === 0) {
    return null;
  }

  // Note: The drawer components render themselves using StackedDrawer
  // internally, so we just need to render the appropriate component
  // for each drawer in the stack.
  // Split view is handled automatically - 2 drawers = split, 1 or 3 = stack
  return (
    <>
      {drawers.map((drawer) => (
        <DrawerContent key={drawer.id} drawer={drawer} />
      ))}
    </>
  );
}

export default DrawerRenderer;
